// Catches the edge case where a provider error frame leaks through the AI SDK
// stream as a plain text part (e.g. when the OpenAI provider streams its own
// error JSON before the AI SDK translates it). The chat would otherwise
// render the raw `{"type":"error",...}` blob inside an assistant bubble.
//
// The corresponding backend fix (toUIMessageStreamResponse({ onError })) is
// the primary cure; this is the defensive backstop on the rendering side.

export interface ParsedStreamError {
    isError: true;
    friendly: string;
}

const FRIENDLY_BY_CODE: Record<string, string> = {
    // OpenAI / Vercel AI SDK provider error codes
    insufficient_quota: "The AI service has exhausted its current quota. Please try again later or contact your administrator.",
    rate_limit_exceeded: "The AI service is busy right now (rate limit reached). Please wait a moment and try again.",
    invalid_api_key: "The AI service rejected the configured credentials. An administrator needs to fix the API key.",
    invalid_authentication: "The AI service rejected the configured credentials. An administrator needs to fix the API key.",
    model_not_found: "The configured AI model is unavailable.",
    context_length_exceeded: "The conversation got too long for the AI model. Start a new chat or remove the attached file and retry.",
    // AWS Lambda authorizer error codes
    AUTH_MISSING: "Your session is missing — please sign in again.",
    AUTH_MALFORMED: "Your session token couldn't be read. Please sign out and sign in again.",
    AUTH_WRONG_TOKEN_USE: "Your session token isn't the right type. Please sign out and sign in again.",
    AUTH_EXPIRED: "Your session has expired — please sign in again.",
    AUTH_INVALID: "Your session is invalid — please sign in again.",
    AUTH_FORBIDDEN: "You don't have permission to use the AI assistant in this workspace.",
};

export function tryParseStreamError(text: string): ParsedStreamError | null {
    if (typeof text !== "string") return null;
    const trimmed = text.trim();
    if (!trimmed.startsWith("{")) return null;
    if (!trimmed.includes('"type"') && !trimmed.includes('"errorCode"') && !trimmed.includes('"ok"')) return null;
    try {
        const obj = JSON.parse(trimmed);
        if (!obj || typeof obj !== "object") return null;

        // Vercel-AI-SDK / OpenAI shape: {"type":"error","error":{"code":"...","message":"..."}}
        if (obj.type === "error") {
            const inner = obj.error || obj;
            const code: string | undefined = inner?.code || inner?.type;
            if (code && FRIENDLY_BY_CODE[code]) {
                return { isError: true, friendly: FRIENDLY_BY_CODE[code] };
            }
            return { isError: true, friendly: "The AI service returned an error. Please try again in a moment." };
        }

        // AWS Lambda authorizer / handler shape: {"ok":false,"errorCode":"AUTH_*","message":"..."}
        if (obj.ok === false && typeof obj.errorCode === "string") {
            const code: string = obj.errorCode;
            if (FRIENDLY_BY_CODE[code]) {
                return { isError: true, friendly: FRIENDLY_BY_CODE[code] };
            }
            if (code.startsWith("AUTH_")) {
                return { isError: true, friendly: "Your session is invalid — please sign in again." };
            }
            return { isError: true, friendly: "The AI service rejected the request. Please try again." };
        }

        return null;
    } catch {
        return null;
    }
}
